import React, { useState, useEffect, useMemo } from 'react';
import { Package, Truck, CheckCircle2, Clock, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1', '#14b8a6', '#f97316'];

function KPICard({ label, value, subtitle, icon, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-100 text-blue-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    red: 'bg-red-50 border-red-100 text-red-700',
    violet: 'bg-violet-50 border-violet-100 text-violet-700',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`${colors[color]} border rounded-xl p-4 transition-all hover:shadow-md`}
      data-testid={`hitachi-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
        <span className="opacity-40">{icon}</span>
      </div>
      <div className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {subtitle && <div className="text-[11px] mt-1 opacity-70">{subtitle}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-3 text-xs">
      <div className="font-semibold text-slate-700 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-semibold text-slate-700">{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

export default function HitachiReport({ projectId, projectName, onRefresh }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSets, setExpandedSets] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    axios.get(`${API}/api/hitachi/report/${projectId}`)
      .then(res => setReportData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [projectId]);

  const summary = reportData?.summary || {};
  const seatMetrics = reportData?.seat_metrics || {};
  const sets = useMemo(() => reportData?.sets || [], [reportData]);
  const dailyLog = useMemo(() => reportData?.daily_log || [], [reportData]);
  const [showDailyLog, setShowDailyLog] = useState(false);
  const [expandedLogDates, setExpandedLogDates] = useState(new Set());
  const [logView, setLogView] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [dueDates, setDueDates] = useState({});
  const [editingDueDates, setEditingDueDates] = useState(false);
  const shortageSets = reportData?.shortage_sets || [];

  // Load due dates from report
  useEffect(() => {
    if (reportData?.due_dates) setDueDates(reportData.due_dates);
  }, [reportData]);

  // Aggregate daily log into weekly/monthly
  const aggregatedLog = useMemo(() => {
    if (logView === 'daily') return dailyLog;

    const buckets = {};
    dailyLog.forEach(entry => {
      let key;
      if (logView === 'weekly') {
        // ISO week: get Monday of the week
        const d = new Date(entry.date + 'T00:00:00');
        const day = d.getDay() || 7;
        d.setDate(d.getDate() - day + 1);
        key = d.toISOString().slice(0, 10);
      } else {
        key = entry.date.slice(0, 7); // YYYY-MM
      }

      if (!buckets[key]) {
        buckets[key] = { key, sets: new Set(), seat_planned: 0, seat_produced: 0, seat_placed: 0, detailMap: {} };
      }
      const b = buckets[key];
      b.sets.add(entry.set_number);
      b.seat_planned += entry.seat_planned;
      b.seat_produced += entry.seat_produced;
      b.seat_placed += entry.seat_placed;

      // Aggregate details by material_code
      (entry.details || []).forEach(d => {
        const dk = d.material_code;
        if (!b.detailMap[dk]) {
          b.detailMap[dk] = { ...d, planned: 0, produced: 0, seat_placed: 0 };
        }
        b.detailMap[dk].planned += d.planned;
        b.detailMap[dk].produced += d.produced;
        b.detailMap[dk].seat_placed += d.seat_placed;
      });
    });

    return Object.values(buckets)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(b => ({
        date: b.key,
        set_number: null,
        sets_list: Array.from(b.sets).sort((a, c) => a - c),
        seat_planned: b.seat_planned,
        seat_produced: b.seat_produced,
        seat_placed: b.seat_placed,
        details: Object.values(b.detailMap),
      }));
  }, [dailyLog, logView]);

  const filteredSets = useMemo(() => {
    if (filterStatus === 'all') return sets;
    return sets.filter(s => s.status === filterStatus);
  }, [sets, filterStatus]);

  const toggleExpand = (setNum) => {
    setExpandedSets(prev => {
      const next = new Set(prev);
      if (next.has(setNum)) next.delete(setNum);
      else next.add(setNum);
      return next;
    });
  };

  const saveDueDates = async () => {
    try {
      await axios.put(`${API}/api/hitachi/due-dates/${projectId}`, { due_dates: dueDates });
      console.log('Due dates saved');
      setEditingDueDates(false);
      
    } catch { console.error('Failed to save due dates'); }
  };

  const statusBadge = (status) => {
    const map = {
      pre_completed: { bg: 'bg-slate-100 text-slate-500', label: 'Pre-completed' },
      completed: { bg: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      shipped: { bg: 'bg-violet-100 text-violet-700', label: 'Shipped' },
      in_progress: { bg: 'bg-amber-100 text-amber-700', label: 'In Progress' },
      not_started: { bg: 'bg-slate-100 text-slate-400', label: 'Not Started' },
    };
    const s = map[status] || map.not_started;
    return <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${s.bg}`}>{s.label}</span>;
  };

  // Seat type distribution for donut chart
  const seatDistribution = useMemo(() => {
    return (reportData?.seat_distribution || []).map(d => ({
      name: d.name.length > 20 ? d.name.substring(0, 20) + '...' : d.name,
      fullName: d.name,
      value: d.value,
      type: d.type,
    }));
  }, [reportData]);

  // Monthly chart data
  const monthlyChart = reportData?.monthly_chart || [];
  const [chartPeriod, setChartPeriod] = useState('daily');

  // Build production activity chart from daily_log
  const activityChartData = useMemo(() => {
    if (!dailyLog.length) return [];
    const buckets = {};
    dailyLog.forEach(entry => {
      let key, label;
      if (chartPeriod === 'daily') {
        key = entry.date;
        label = entry.date.slice(5); // MM-DD
      } else if (chartPeriod === 'weekly') {
        const d = new Date(entry.date + 'T00:00:00');
        const day = d.getDay() || 7;
        d.setDate(d.getDate() - day + 1);
        key = d.toISOString().slice(0, 10);
        label = 'W' + key.slice(5);
      } else {
        key = entry.date.slice(0, 7);
        const d = new Date(key + '-01T00:00:00');
        label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }
      if (!buckets[key]) buckets[key] = { key, label, planned: 0, produced: 0, seat_placed: 0 };
      buckets[key].planned += entry.seat_planned;
      buckets[key].produced += entry.seat_produced;
      buckets[key].seat_placed += entry.seat_placed;
    });
    return Object.values(buckets).sort((a, b) => a.key.localeCompare(b.key));
  }, [dailyLog, chartPeriod]);

  // Seat type totals table
  const seatTypeTotals = reportData?.seat_type_totals || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-12 text-slate-400">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No report data available</p>
      </div>
    );
  }

  const completionPercent = summary.total_sets > 0 ? Math.round((summary.completed / summary.total_sets) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="hitachi-report">
      {/* KPI Cards - Set Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="hitachi-kpi-cards">
        <KPICard label="Total Sets Ordered" value={summary.total_sets} icon={<Package className="w-4 h-4" />} color="blue"
          subtitle={`${reportData?.pre_completed || 0} pre-completed`} />
        <KPICard label="Completed" value={summary.completed} icon={<CheckCircle2 className="w-4 h-4" />} color="emerald"
          subtitle={`${completionPercent}% of total`} />
        <KPICard label="Shipped" value={summary.shipped} icon={<Truck className="w-4 h-4" />} color="violet"
          subtitle={`${Math.max(0, summary.completed - summary.shipped)} awaiting ship`} />
        <KPICard label="In Progress" value={summary.in_progress} icon={<Clock className="w-4 h-4" />} color="amber" />
        <KPICard label="Remaining" value={summary.remaining} icon={<AlertTriangle className="w-4 h-4" />} color="red"
          subtitle={`${summary.not_started || 0} not started`} />
        <KPICard label="Shortage" value={summary.shortage || 0} icon={<AlertTriangle className="w-4 h-4" />} color="red"
          subtitle={summary.shortage > 0 ? `${summary.shortage} past due date` : 'On track'} />
      </div>

      {/* Seat Metrics KPIs - Descriptive */}
      {seatMetrics.seat_per_set > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hitachi-seat-metrics">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 hover:shadow-md transition-all" data-testid="hitachi-kpi-seats-produced">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500">Seats Produced</span>
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 font-bold">seat</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">{(seatMetrics.total_seats_produced || 0).toLocaleString()}</div>
            <div className="text-[11px] mt-1 text-blue-400">of {(seatMetrics.total_seats_required || 0).toLocaleString()} required</div>
            <div className="text-[10px] mt-0.5 text-blue-300">{seatMetrics.count_2p || 0}x 2P + {seatMetrics.count_1p || 0}x 1P per set = {seatMetrics.seat_per_set} seat</div>
            <div className="w-full h-1.5 bg-blue-200 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${seatMetrics.total_seats_required ? Math.min(100, Math.round(seatMetrics.total_seats_produced / seatMetrics.total_seats_required * 100)) : 0}%` }} />
            </div>
          </div>
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 hover:shadow-md transition-all" data-testid="hitachi-kpi-seat-placed">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">Seat Placed</span>
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 font-bold">seat placed</span>
            </div>
            <div className="text-2xl font-bold text-indigo-700">{(seatMetrics.seat_placed || 0).toLocaleString()}</div>
            <div className="text-[11px] mt-1 text-indigo-400">of {(seatMetrics.seat_placed_target || 0).toLocaleString()} target</div>
            <div className="text-[10px] mt-0.5 text-indigo-300">{seatMetrics.count_2p || 0}x 2P = {(seatMetrics.count_2p || 0) * 2} + {seatMetrics.count_1p || 0}x 1P = {seatMetrics.count_1p || 0} = {seatMetrics.seat_placed_per_set} / set</div>
            <div className="w-full h-1.5 bg-indigo-200 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${seatMetrics.seat_placed_target ? Math.min(100, Math.round(seatMetrics.seat_placed || 0) / seatMetrics.seat_placed_target * 100) : 0}%` }} />
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 hover:shadow-md transition-all" data-testid="hitachi-kpi-seats-planned">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500">Seats Planned</span>
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 font-bold">seat</span>
            </div>
            <div className="text-2xl font-bold text-emerald-700">{(seatMetrics.total_seats_planned || 0).toLocaleString()}</div>
            <div className="text-[11px] mt-1 text-emerald-400">{seatMetrics.seat_per_set} seat / set</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all" data-testid="hitachi-kpi-parts-per-set">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Parts Per Set</span>
            </div>
            <div className="text-2xl font-bold text-slate-700">{seatMetrics.seat_per_set + seatMetrics.other_per_set}</div>
            <div className="text-[11px] mt-1 text-slate-400">{seatMetrics.count_2p || 0}x 2P + {seatMetrics.count_1p || 0}x 1P + {seatMetrics.other_per_set} onsert</div>
            <div className="text-[10px] text-slate-300">{seatMetrics.seat_per_set} seat + {seatMetrics.other_per_set} other = {seatMetrics.seat_per_set + seatMetrics.other_per_set}</div>
          </div>
        </div>
      )}

      {/* Shortage Alert */}
      {shortageSets.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="hitachi-shortage-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-bold text-red-700">Shortage: {shortageSets.length} set{shortageSets.length > 1 ? 's' : ''} past due date</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {shortageSets.map(s => (
              <span key={s.set_number} className="px-2 py-1 bg-red-100 text-red-700 text-[10px] font-semibold rounded-md">
                Set #{s.set_number} (due: {s.due_date}, status: {s.status})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="hitachi-progress-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-slate-700">Overall Progress</span>
          <span className="text-sm font-bold text-indigo-600">{completionPercent}%</span>
        </div>
        <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden flex">
          {reportData?.pre_completed > 0 && (
            <div className="h-full bg-slate-300" style={{ width: `${(reportData.pre_completed / summary.total_sets) * 100}%` }}
              title={`Pre-completed: ${reportData.pre_completed}`} />
          )}
          <div className="h-full bg-violet-400" style={{ width: `${((summary.shipped - (reportData?.pre_completed || 0)) / summary.total_sets) * 100}%` }}
            title={`Shipped: ${summary.shipped - (reportData?.pre_completed || 0)}`} />
          <div className="h-full bg-emerald-400" style={{ width: `${((summary.completed - summary.shipped) / summary.total_sets) * 100}%` }}
            title={`Completed (not shipped): ${summary.completed - summary.shipped}`} />
          <div className="h-full bg-amber-300" style={{ width: `${(summary.in_progress / summary.total_sets) * 100}%` }}
            title={`In Progress: ${summary.in_progress}`} />
        </div>
        <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-300" /> Pre-completed ({reportData?.pre_completed || 0})</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-violet-400" /> Shipped ({Math.max(0, summary.shipped - (reportData?.pre_completed || 0))})</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-400" /> Completed ({Math.max(0, summary.completed - summary.shipped)})</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-300" /> In Progress ({summary.in_progress})</span>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Production Activity Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="hitachi-activity-chart">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-800">Production Activity</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">Planned, produced and seat placed over time</p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="chart-period-toggle">
              {[
                { id: 'daily', label: 'Daily' },
                { id: 'weekly', label: 'Weekly' },
                { id: 'monthly', label: 'Monthly' },
              ].map(v => (
                <button key={v.id} onClick={() => setChartPeriod(v.id)}
                  className={`px-2.5 py-1 text-[9px] font-semibold rounded-md transition-all ${
                    chartPeriod === v.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                  }`} data-testid={`chart-period-${v.id}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {activityChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={activityChartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="planned" name="Planned" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="produced" name="Produced" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="seat_placed" name="Seat Placed" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-300 text-sm">No production data yet</div>
          )}
        </div>

        {/* Seat Type Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="hitachi-seat-distribution">
          <div className="mb-3">
            <h2 className="text-base font-bold text-slate-800">Seat Type Distribution</h2>
            <p className="text-[10px] text-slate-400 mt-0.5">Total produced by seat type across all sets</p>
          </div>
          {seatDistribution.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={seatDistribution} cx="50%" cy="50%" innerRadius={50} outerRadius={95}
                  paddingAngle={2} dataKey="value"
                  label={({ name, percent }) => percent > 0.03 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                  labelLine={{ strokeWidth: 1, stroke: '#cbd5e1' }}>
                  {seatDistribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-300 text-sm">No production data yet</div>
          )}
        </div>
      </div>

      {/* Active Set Progress - shows seat-type breakdown for in-progress/recently completed sets */}
      {(() => {
        const activeSets = sets.filter(s => s.status === 'in_progress' || s.status === 'completed');
        if (activeSets.length === 0) return null;
        const seatTypesList = reportData?.seat_types || [];
        return (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="hitachi-active-set-progress">
            <div className="p-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-800">Active Set Progress</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">Seat-type completion for in-progress and recently completed sets</p>
            </div>
            <div className="p-4 space-y-4">
              {activeSets.slice(0, 10).map(s => {
                const pct = s.total_required > 0 ? Math.round((s.total_produced / s.total_required) * 100) : 0;
                const isComp = s.status === 'completed' || s.status === 'shipped';
                return (
                  <div key={s.set_number} className={`border rounded-lg p-3 ${isComp ? 'border-emerald-200 bg-emerald-50/20' : 'border-amber-200 bg-amber-50/20'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-800">Set #{s.set_number}</span>
                        {statusBadge(s.status)}
                        <span className="text-[10px] text-slate-400">{s.start_date || ''}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-600">{s.total_produced}/{s.total_required} ({pct}%)</span>
                    </div>
                    {/* Per seat type mini progress bars */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      {(s.details || []).map(d => {
                        const detPct = d.qty_per_set > 0 ? Math.round((d.produced / d.qty_per_set) * 100) : 0;
                        const seatInfo = seatTypesList.find(st => st.material_code === d.material_code);
                        return (
                          <div key={d.material_code} className="bg-white/80 rounded p-1.5 border border-slate-100">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[9px] font-medium text-slate-600 truncate max-w-[120px]">{seatInfo?.name || d.name}</span>
                              <span className={`text-[9px] font-bold ${d.complete ? 'text-emerald-600' : 'text-slate-500'}`}>{d.produced}/{d.qty_per_set}</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${d.complete ? 'bg-emerald-500' : detPct > 0 ? 'bg-amber-400' : 'bg-slate-200'}`}
                                style={{ width: `${Math.min(100, detPct)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Seat Type Totals Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="hitachi-seat-type-totals">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">Seat Type Totals</h2>
          <p className="text-[10px] text-slate-400 mt-0.5">Aggregate production across all sets</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                <th className="px-4 py-2 text-left font-semibold">Seat Type</th>
                <th className="px-3 py-2 text-center font-semibold">Type</th>
                <th className="px-3 py-2 text-right font-semibold">Per Set</th>
                <th className="px-3 py-2 text-right font-semibold">Total Required</th>
                <th className="px-3 py-2 text-right font-semibold text-emerald-500">Total Produced</th>
                <th className="px-3 py-2 text-right font-semibold text-red-400">Remaining</th>
                <th className="px-3 py-2 text-center font-semibold">Progress</th>
              </tr>
            </thead>
            <tbody>
              {seatTypeTotals.map((st, i) => {
                const remaining = Math.max(0, st.total_required - st.total_produced);
                const pct = st.total_required > 0 ? Math.round((st.total_produced / st.total_required) * 100) : 0;
                return (
                  <tr key={st.name + i} className={`${i % 2 === 0 ? '' : 'bg-slate-50/30'} hover:bg-blue-50/20`}>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{st.name}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${st.type === 'other' ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-600'}`}>
                        {st.type === 'other' ? 'OTHER' : 'SEAT'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-600">{st.qty_per_set}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-blue-600">{st.total_required.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-emerald-600">{st.total_produced.toLocaleString()}</td>
                    <td className={`px-3 py-2.5 text-right font-semibold ${remaining > 0 ? 'text-red-500' : 'text-slate-300'}`}>{remaining.toLocaleString()}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 justify-center">
                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-indigo-400'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <span className="text-[9px] font-bold text-slate-500">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Production Log */}
      {dailyLog.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="hitachi-daily-log">
          <button onClick={() => setShowDailyLog(!showDailyLog)}
            className="w-full p-4 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="daily-log-toggle">
            <div>
              <h2 className="text-base font-bold text-slate-800">Production Log</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">{dailyLog.length} entries across all sets and days</p>
            </div>
            {showDailyLog ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {showDailyLog && (
            <div>
              {/* View Toggle */}
              <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-1 bg-slate-50/50" data-testid="log-view-toggle">
                {[
                  { id: 'daily', label: 'Daily' },
                  { id: 'weekly', label: 'Weekly' },
                  { id: 'monthly', label: 'Monthly' },
                ].map(v => (
                  <button key={v.id} onClick={() => { setLogView(v.id); setExpandedLogDates(new Set()); }}
                    className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all ${
                      logView === v.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600 bg-white'
                    }`} data-testid={`log-view-${v.id}`}>
                    {v.label}
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                      <th className="px-3 py-2 text-left font-semibold w-24">
                        {logView === 'daily' ? 'Date' : logView === 'weekly' ? 'Week Start' : 'Month'}
                      </th>
                      <th className="px-3 py-2 text-center font-semibold w-20">
                        {logView === 'daily' ? 'Set #' : 'Sets'}
                      </th>
                      <th className="px-3 py-2 text-right font-semibold w-16 text-blue-500">Planned</th>
                      <th className="px-3 py-2 text-right font-semibold w-16 text-emerald-500">Produced</th>
                      <th className="px-3 py-2 text-right font-semibold w-20 text-indigo-500">Seat Placed</th>
                      <th className="px-3 py-2 text-center font-semibold w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregatedLog.map((entry, idx) => {
                      const logKey = logView === 'daily'
                        ? `${entry.date}-${entry.set_number}`
                        : `${logView}-${entry.date}`;
                      const isOpen = expandedLogDates.has(logKey);
                      const dateLabel = logView === 'monthly'
                        ? new Date(entry.date + '-01T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
                        : entry.date;

                      return (
                        <React.Fragment key={idx}>
                          <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => {
                              setExpandedLogDates(prev => {
                                const next = new Set(prev);
                                if (next.has(logKey)) next.delete(logKey); else next.add(logKey);
                                return next;
                              });
                            }}>
                            <td className="px-3 py-2 font-semibold text-slate-700">{dateLabel}</td>
                            <td className="px-3 py-2 text-center font-bold text-indigo-600">
                              {logView === 'daily'
                                ? `#${entry.set_number}`
                                : entry.sets_list?.map(s => `#${s}`).join(', ')}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-blue-600">{entry.seat_planned}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-600">{entry.seat_produced}</td>
                            <td className="px-3 py-2 text-right font-bold text-indigo-600">{entry.seat_placed}</td>
                            <td className="px-3 py-2 text-center">
                              {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 inline" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 inline" />}
                            </td>
                          </tr>
                          {isOpen && entry.details?.map((d, di) => (
                            <tr key={di} className="bg-indigo-50/30 border-t border-indigo-100/50">
                              <td className="px-3 py-1.5 pl-8 text-[10px] text-slate-500">{d.type === 'other' ? 'OTH' : 'SEAT'}</td>
                              <td className="px-3 py-1.5 text-[10px] font-medium text-slate-700">
                                <div>{d.name}</div>
                                <div className="text-[9px] text-slate-400">{d.material_code}</div>
                              </td>
                              <td className="px-3 py-1.5 text-right text-[10px] font-semibold text-blue-600">{d.planned}</td>
                              <td className="px-3 py-1.5 text-right text-[10px] font-semibold text-emerald-600">{d.produced}</td>
                              <td className="px-3 py-1.5 text-right text-[10px] font-semibold text-indigo-600">{d.seat_placed}</td>
                              <td></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Set Detail Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="hitachi-set-table">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-800">Set Details</h2>
            <p className="text-[10px] text-slate-400 mt-0.5">{filteredSets.length} of {sets.length} sets shown</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setEditingDueDates(!editingDueDates); }}
              className={`px-3 py-1.5 text-[10px] font-semibold rounded-md transition-all border ${
                editingDueDates ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
              }`} data-testid="edit-due-dates-btn">
              {editingDueDates ? 'Editing Due Dates...' : 'Set Due Dates'}
            </button>
            {editingDueDates && (
              <button onClick={saveDueDates}
                className="px-3 py-1.5 text-[10px] font-bold bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                data-testid="save-due-dates-btn">
                Save
              </button>
            )}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="set-filter">
            {[
              { id: 'all', label: 'All' },
              { id: 'pre_completed', label: 'Pre-comp' },
              { id: 'completed', label: 'Completed' },
              { id: 'shipped', label: 'Shipped' },
              { id: 'in_progress', label: 'In Progress' },
              { id: 'not_started', label: 'Not Started' },
            ].map(f => (
              <button key={f.id} onClick={() => setFilterStatus(f.id)}
                className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all ${
                  filterStatus === f.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                }`} data-testid={`filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                <th className="px-2 py-2 text-center font-semibold w-8"></th>
                <th className="px-3 py-2 text-left font-semibold">Set #</th>
                <th className="px-3 py-2 text-center font-semibold">Status</th>
                <th className="px-3 py-2 text-center font-semibold text-amber-500">Due Date</th>
                <th className="px-3 py-2 text-center font-semibold">Start Date</th>
                <th className="px-3 py-2 text-center font-semibold">Completion</th>
                <th className="px-3 py-2 text-center font-semibold">Ship Date</th>
                <th className="px-3 py-2 text-right font-semibold">Produced</th>
                <th className="px-3 py-2 text-right font-semibold">Required</th>
                <th className="px-3 py-2 text-center font-semibold">Progress</th>
              </tr>
            </thead>
            <tbody>
              {filteredSets.map((s, i) => {
                const isExpanded = expandedSets.has(s.set_number);
                const pct = s.total_required > 0 ? Math.round((s.total_produced / s.total_required) * 100) : 0;
                return (
                  <React.Fragment key={s.set_number}>
                    <tr className={`${i % 2 === 0 ? '' : 'bg-slate-50/30'} hover:bg-indigo-50/20 cursor-pointer transition-colors`}
                      onClick={() => s.status !== 'not_started' && toggleExpand(s.set_number)}
                      data-testid={`set-row-${s.set_number}`}>
                      <td className="px-2 py-2 text-center">
                        {s.status !== 'not_started' && s.status !== 'pre_completed' ? (
                          isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-indigo-500 inline" /> :
                          <ChevronDown className="w-3.5 h-3.5 text-slate-300 inline" />
                        ) : <span className="text-slate-200">-</span>}
                      </td>
                      <td className="px-3 py-2 font-bold text-slate-700">#{s.set_number}</td>
                      <td className="px-3 py-2 text-center">{statusBadge(s.status)}</td>
                      <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()} data-testid={`due-date-${s.set_number}`}>
                        {editingDueDates ? (
                          <input type="date" value={dueDates[String(s.set_number)] || ''}
                            onChange={e => setDueDates(prev => ({ ...prev, [String(s.set_number)]: e.target.value }))}
                            className="border border-amber-300 rounded px-1 py-0.5 text-[10px] w-28 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                        ) : (
                          <span className={`font-medium ${
                            dueDates[String(s.set_number)] && dueDates[String(s.set_number)] < new Date().toISOString().slice(0, 10) && s.status !== 'shipped' && s.status !== 'pre_completed'
                              ? 'text-red-500 font-bold' : 'text-amber-600'
                          }`}>
                            {dueDates[String(s.set_number)] || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-500">{s.start_date || '-'}</td>
                      <td className="px-3 py-2 text-center font-medium text-emerald-600">{s.completion_date || (s.status === 'pre_completed' ? 'Previous' : '-')}</td>
                      <td className="px-3 py-2 text-center font-medium text-violet-600">{s.ship_date || (s.status === 'pre_completed' ? 'Previous' : '-')}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-600">{s.total_produced?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{s.total_required?.toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-400' : 'bg-slate-200'}`}
                              style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                          <span className="text-[9px] font-bold text-slate-500 min-w-[28px] text-right">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded detail row */}
                    {isExpanded && s.details && (
                      <tr>
                        <td colSpan={10} className="bg-indigo-50/30 px-6 py-3 border-t border-indigo-100">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="text-[9px] text-slate-400 uppercase tracking-wider">
                                <th className="py-1 text-left font-semibold">Seat Type</th>
                                <th className="py-1 text-center font-semibold w-12">Type</th>
                                <th className="py-1 text-right font-semibold w-16">Per Set</th>
                                <th className="py-1 text-right font-semibold w-16 text-blue-500">PLN</th>
                                <th className="py-1 text-right font-semibold w-16 text-emerald-500">Done</th>
                                <th className="py-1 text-right font-semibold w-16 text-orange-500">Remain</th>
                                <th className="py-1 text-center font-semibold w-24">Progress</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.details.map(d => {
                                const pct = d.qty_per_set > 0 ? Math.round((d.produced / d.qty_per_set) * 100) : 0;
                                return (
                                  <tr key={d.material_code} className={`border-t border-slate-100/60 ${d.complete ? 'bg-emerald-50/20' : ''}`}>
                                    <td className="py-1.5 font-medium text-slate-700">
                                      <div>{d.name}</div>
                                      <div className="text-[9px] text-slate-400">{d.material_code}</div>
                                    </td>
                                    <td className="py-1.5 text-center">
                                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${d.type === 'other' ? 'bg-slate-100 text-slate-400' : 'bg-blue-50 text-blue-500'}`}>
                                        {d.type === 'other' ? 'OTH' : 'SEAT'}
                                      </span>
                                    </td>
                                    <td className="py-1.5 text-right font-semibold text-slate-600">{d.qty_per_set}</td>
                                    <td className="py-1.5 text-right font-bold text-blue-600">{d.planned || 0}</td>
                                    <td className="py-1.5 text-right font-bold text-emerald-600">{d.produced}</td>
                                    <td className={`py-1.5 text-right font-bold ${d.remaining > 0 ? 'text-orange-500' : 'text-slate-300'}`}>{d.remaining}</td>
                                    <td className="py-1.5">
                                      <div className="flex items-center gap-2 justify-center">
                                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                          <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-blue-400' : 'bg-slate-200'}`}
                                            style={{ width: `${Math.min(100, pct)}%` }} />
                                        </div>
                                        <span className="text-[9px] font-bold text-slate-500 min-w-[28px] text-right">{pct}%</span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredSets.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No sets match this filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
